import { ArrowDown, ArrowUp, Copy, CornerLeftUp, EyeOff, Pencil, Trash2 } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { parentElementId } from './editorElement';

/**
 * What the toolbar over the selection can ask for, and how much of it the wire already carries.
 *
 * The canvas performs none of these. The builder owns the draft, so a button posts an intent and
 * waits to be told what to draw — exactly as a keystroke in the rail does. This union is local on
 * purpose: `theme-editor.schema.ts` is shared with the admin, and until a message for an intent
 * lands there the toolbar draws that button disabled rather than inventing a payload nobody parses.
 */
export type EditorIntentKind = 'edit' | 'selectParent' | 'moveUp' | 'moveDown' | 'duplicate' | 'hide' | 'delete';

/** One asked-for change, named against whatever is selected at the moment the button is pressed. */
export type EditorIntent = { kind: EditorIntentKind; elementId: string | null };

/** The payload an intent travels to the builder as. Every kind now has one; none is invented here. */
export type IntentMessage =
  | { type: 'clicked'; elementId: string | null; styleGroup?: string }
  | { type: 'intent'; intent: Exclude<EditorIntentKind, 'selectParent'>; elementId: string }
  | { type: 'insert'; index: number; parentId: string | null };

/** One button in the toolbar: what it asks for, what it is called, and what it is drawn as. */
export type IntentAction = {
  kind: EditorIntentKind;
  label: string;
  Icon: ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;
};

/**
 * The toolbar's first pill: open the selection's editor — a click on the page only shows where the element
 * lives in Layers, so this is the press that edits it — then, in LightFunnels' own order, rise a level, copy
 * it, remove it.
 * Theirs also carries paste and copy-styles; neither has a message on this wire, and a button that only
 * ever draws itself disabled is worse than no button, so the pill is what this builder can perform.
 */
export const INTENT_ACTIONS: readonly IntentAction[] = [
  { kind: 'edit', label: 'Edit', Icon: Pencil },
  { kind: 'selectParent', label: 'Select parent', Icon: CornerLeftUp },
  { kind: 'duplicate', label: 'Duplicate', Icon: Copy },
  { kind: 'hide', label: 'Hide', Icon: EyeOff },
  { kind: 'delete', label: 'Delete', Icon: Trash2 }
];

/** The second pill, which LightFunnels keeps apart from the first: where this sits among its siblings. */
export const MOVE_ACTIONS: readonly IntentAction[] = [
  { kind: 'moveUp', label: 'Move up', Icon: ArrowUp },
  { kind: 'moveDown', label: 'Move down', Icon: ArrowDown }
];

/** Why a button is disabled: the shared schema has no message for it yet, and the canvas will not invent one. */
export const UNSUPPORTED_REASON = 'Not wired yet: the shared editor schema carries no message for this';

// Turns one intent into a message the builder understands. Rising a level is an ordinary `clicked`,
// naming the parent to select; everything else is an `intent` the builder performs against its draft.
export const intentMessage = (intent: EditorIntent): IntentMessage | null => {
  if (!intent.elementId) return null;
  if (intent.kind === 'selectParent') return { type: 'clicked', elementId: parentElementId(intent.elementId) };
  return { type: 'intent', intent: intent.kind, elementId: intent.elementId };
};

// Reports whether the wire can carry this intent today, which is what decides if its button is live.
export const intentSupported = (kind: EditorIntentKind): boolean =>
  intentMessage({ kind, elementId: 'probe' }) !== null;

/**
 * Adding something at a boundary, which the toolbar has no button for: it is asked for at a gap in a
 * list rather than against something selected, so it names a position and not an id. `parentId` is the
 * container that list belongs to, and null is the page's own roots.
 */
export type EditorInsertIntent = { kind: 'addHere'; parentId: string | null; index: number };

/** Whether the wire can carry an insertion. `EditorInsertSchema` names a position, so it can. */
export const INSERT_SUPPORTED: boolean = true;

/** Why an insertion point would be drawn inert, which nothing does today. */
export const INSERT_REASON = 'Nothing can be added here';

// Asks the builder to open the library at one boundary. It names a place, because nothing exists there yet.
export const insertMessage = (intent: EditorInsertIntent): IntentMessage =>
  ({ type: 'insert', index: intent.index, parentId: intent.parentId });

/** Whether a drop inside a container can be reported. `EditorDroppedSchema.parentId` names the holder, so it can. */
export const DEEP_DROP_SUPPORTED: boolean = true;

/** Kept for a boundary the builder has said it will not take, which is the only line still drawn inert. */
export const DEEP_DROP_NOTE = 'not here';
